import INVENTORY_API_BASE_URL from "../../config/api";
import { apiFetch } from "../../config/apiClient";
import React, { useEffect, useState, useRef } from "react";

import "./log.css";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

function Log() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const actionDetailsRef = useRef(null);
  const activityDetailsRef = useRef(null);
  const userDetailsRef = useRef(null);

  useEffect(() => {
    apiFetch(`${INVENTORY_API_BASE_URL}/get-employee-activities`)
      .then((response) => response.json())
      .then((data) => {
        setActivities(
          Array.isArray(data) ? data : data.data || data.result || []
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedAction && actionDetailsRef.current) {
      actionDetailsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedAction]);

  useEffect(() => {
    if (selectedActivity && activityDetailsRef.current) {
      activityDetailsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedActivity]);

  useEffect(() => {
    if (selectedUser && userDetailsRef.current) {
      userDetailsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedUser]);

  const handleView = (activity) => {
    setSelectedActivity(activity);
  };

  const handleCloseDetails = () => {
    setSelectedActivity(null);
  };

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setSelectedUser(null);
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setSelectedAction(null);
  };

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return (
        <ul>
          {value.map((item, idx) => (
            <li key={idx}>{renderValue(item)}</li>
          ))}
        </ul>
      );
    } else if (typeof value === "object" && value !== null) {
      return (
        <ul>
          {Object.entries(value).map(([key, val]) => (
            <li key={key}>
              <strong>{key}:</strong> {renderValue(val)}
            </li>
          ))}
        </ul>
      );
    } else {
      return value?.toString() || "N/A";
    }
  };

  const actionCounts = activities.reduce((acc, activity) => {
    acc[activity.action] = (acc[activity.action] || 0) + 1;
    return acc;
  }, {});

  const userCounts = activities.reduce((acc, activity) => {
    acc[activity.name] = (acc[activity.name] || 0) + 1;
    return acc;
  }, {});

  const actionData = Object.entries(actionCounts)
    .map(([action, count]) => ({
      name: action,
      value: count,
    }))
    .sort((left, right) => right.value - left.value);


  const COLORS = ["#256b4b", "#4a9470", "#87ad71", "#c1a956", "#bf7655", "#4a9090", "#6c7fa8", "#8a9a73"];

  const groupedByDate = activities.reduce((acc, activity) => {
    const date = new Date(activity.timestamp).toLocaleDateString("en-GB");
    if (!acc[date]) acc[date] = {};
    if (!acc[date][activity.name]) acc[date][activity.name] = 0;
    acc[date][activity.name]++;
    return acc;
  }, {});

  const timelineData = Object.entries(groupedByDate).map(([date, users]) => ({
    date,
    ...users,
  }));

  return (
    <main className="log-container">
      <div className="log-content">
        <header className="log-header">
          <div>
            <p className="log-eyebrow">Audit intelligence</p>
            <h1>Activity control center</h1>
            <p className="log-subtitle">
              Explore staff activity by action and by user. Select a chart item to inspect its records.
            </p>
          </div>
          <span className="log-header-badge">
            <span className={"log-status-dot" + (loading ? " is-loading" : "")} />
            {loading ? "Loading activity" : "Activity tracking"}
          </span>
        </header>

        {!loading && activities.length > 0 && (
          <>
            <section className="log-summary" aria-label="Activity log summary">
              <article className="log-summary-card log-summary-card--accent">
                <span className="log-summary-label">Total activities</span>
                <strong>{activities.length.toLocaleString()}</strong>
                <span className="log-summary-note">Recorded events</span>
              </article>
              <article className="log-summary-card">
                <span className="log-summary-label">People active</span>
                <strong>{Object.keys(userCounts).length.toLocaleString()}</strong>
                <span className="log-summary-note">Unique staff users</span>
              </article>
              <article className="log-summary-card">
                <span className="log-summary-label">Activity types</span>
                <strong>{actionData.length.toLocaleString()}</strong>
                <span className="log-summary-note">Available event groups</span>
              </article>
            </section>

            <section className="charts-section" aria-label="Activity analytics">
              <div className="charts-wrapper">
                <article className="chart-card">
                  <div className="chart-heading">
                    <div>
                      <span className="chart-kicker">Activity breakdown</span>
                      <h2>Activity type distribution</h2>
                      <p>Select a bar to inspect the matching activity records.</p>
                    </div>
                    <span className="chart-count">{actionData.length} types</span>
                  </div>
                  <div className="chart-plot">
                    <ResponsiveContainer width="100%" height={330}>
                      <BarChart
                        data={actionData}
                        layout="vertical"
                        margin={{ top: 6, right: 20, left: 8, bottom: 6 }}
                        onClick={(data) => {
                          if (data && data.activeLabel)
                            handleActionClick(data.activeLabel);
                        }}
                      >
                        <CartesianGrid vertical={false} stroke="#e7eee8" strokeDasharray="4 5" />
                        <XAxis
                          type="number"
                          allowDecimals={false}
                          tick={{ fontSize: 10, fill: "#728276" }}
                          axisLine={false}
                          tickLine={false}
                        />
                        
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={138}
                          interval={0}
                          tick={{ fontSize: 10, fill: "#53675a" }}
                          axisLine={false}
                          tickLine={false}
                        />
                        
                        <Tooltip
                          contentStyle={{
                            borderRadius: "10px",
                            backgroundColor: "#fff",
                            border: "1px solid #e2e9f1",
                            boxShadow: "0 8px 24px rgba(27, 49, 79, 0.12)",
                          }}
                          formatter={(value) => [`${value} activities`, "Total"]}
                        />
                        <Legend verticalAlign="top" height={28} />
                        <Bar dataKey="value" name="Activities" barSize={22} radius={[0, 7, 7, 0]}>
                          {actionData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </article>

                <article className="chart-card">
                  <div className="chart-heading">
                    <div>
                      <span className="chart-kicker">Daily activity</span>
                      <h2>User activity trend</h2>
                      <p>Select a data point to view one user’s activity history.</p>
                    </div>
                    <span className="chart-count">{Object.keys(userCounts).length} users</span>
                  </div>
                  <div className="chart-plot">
                    <ResponsiveContainer width="100%" height={330}>
                      <LineChart
                        data={timelineData}
                        margin={{ top: 12, right: 18, left: 0, bottom: 8 }}
                      >
                        <CartesianGrid vertical={false} stroke="#e7eee8" strokeDasharray="4 5" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10, fill: "#728276" }}
                          interval="preserveStartEnd"
                          axisLine={{ stroke: "#e5ebf2" }}
                          tickLine={false}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 11, fill: "#728276" }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip contentStyle={{ border: "1px solid #dce8de", borderRadius: "9px", boxShadow: "0 8px 22px rgba(22, 55, 40, 0.12)" }} />
                        <Legend />
                        {Object.keys(userCounts).map((user, idx) => (
                          <Line
                            key={user}
                            type="monotone"
                            dataKey={user}
                            stroke={COLORS[idx % COLORS.length]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{
                              r: 6,
                              onClick: () => handleUserClick(user),
                            }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </article>
              </div>
            </section>
          </>
        )}

        {loading && (
          <section className="log-state" role="status">
            <span className="log-spinner" aria-hidden="true" />
            <strong>Loading activity records</strong>
            <span>Fetching the latest staff activity.</span>
          </section>
        )}

        {!loading && activities.length === 0 && (
          <section className="log-state" aria-live="polite">
            <span className="log-state-icon" aria-hidden="true">◷</span>
            <strong>No activity records found</strong>
            <span>Activity details will appear here when staff actions are recorded.</span>
          </section>
        )}

        {selectedAction && (
          <section className="log-details action-details" ref={actionDetailsRef}>
            <div className="details-header">
              <div>
                <span className="details-kicker">Filtered activity</span>
                <h2>Action: <span>{selectedAction}</span></h2>
              </div>
              <button
                type="button"
                className="close-button"
                onClick={() => setSelectedAction(null)}
              >
                Close details
              </button>
            </div>
            <div className="log-table-scroll">
              <table className="log-table">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Activity</th>
                    <th scope="col">Date &amp; time</th>
                    <th scope="col">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {activities
                    .filter((activity) => activity.action === selectedAction)
                    .map((activity, index) => (
                      <tr key={index}>
                        <td>{activity.name || "N/A"}</td>
                        <td>{activity.email || "N/A"}</td>
                        <td>{activity.action || "N/A"}</td>
                        <td>
                          {activity.timestamp
                            ? new Date(activity.timestamp).toLocaleString()
                            : "N/A"}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="action-button"
                            onClick={() => handleView(activity)}
                          >
                            View activity
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {selectedUser && (
          <section className="log-details user-details" ref={userDetailsRef}>
            <div className="details-header">
              <div>
                <span className="details-kicker">Filtered activity</span>
                <h2>User: <span>{selectedUser}</span></h2>
              </div>
              <button
                type="button"
                className="close-button"
                onClick={() => setSelectedUser(null)}
              >
                Close details
              </button>
            </div>
            <div className="log-table-scroll">
              <table className="log-table">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Activity</th>
                    <th scope="col">Date &amp; time</th>
                    <th scope="col">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {activities
                    .filter((activity) => activity.name === selectedUser)
                    .map((activity, index) => (
                      <tr key={index}>
                        <td>{activity.name || "N/A"}</td>
                        <td>{activity.email || "N/A"}</td>
                        <td>{activity.action || "N/A"}</td>
                        <td>
                          {activity.timestamp
                            ? new Date(activity.timestamp).toLocaleString()
                            : "N/A"}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="action-button"
                            onClick={() => handleView(activity)}
                          >
                            View activity
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {selectedActivity && (
          <section className="log-details activity-details" ref={activityDetailsRef}>
            <div className="details-header">
              <div>
                <span className="details-kicker">Record inspection</span>
                <h2>Activity details</h2>
                <p>Recorded {selectedActivity.timestamp
                  ? new Date(selectedActivity.timestamp).toLocaleString()
                  : "time unavailable"}</p>
              </div>
              <button
                type="button"
                className="close-button"
                onClick={handleCloseDetails}
              >
                Close details
              </button>
            </div>

            <div className="activity-overview-grid">
              <article>
                <span>User</span>
                <strong>{selectedActivity.name || "N/A"}</strong>
              </article>
              <article>
                <span>Email</span>
                <strong>{selectedActivity.email || "N/A"}</strong>
              </article>
              <article>
                <span>Action</span>
                <strong>{selectedActivity.action || "N/A"}</strong>
              </article>
            </div>

            {selectedActivity.details &&
            selectedActivity.details.oldData &&
            selectedActivity.details.newData ? (
              <section className="activity-change-section">
                <div className="detail-section-heading">
                  <h3>Changed fields</h3>
                  <span>Before and after</span>
                </div>
                <div className="log-table-scroll">
                  <table className="log-table">
                    <thead>
                      <tr>
                        <th scope="col">Field</th>
                        <th scope="col">Old value</th>
                        <th scope="col">New value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(selectedActivity.details.newData).map(
                        (key) => {
                          const oldValue = selectedActivity.details.oldData[key];
                          const newValue = selectedActivity.details.newData[key];
                          const isChanged =
                            JSON.stringify(oldValue) !== JSON.stringify(newValue);

                          if (!isChanged) return null;

                          return (
                            <tr key={key}>
                              <td>{key}</td>
                              <td className="log-diff-old">{renderValue(oldValue)}</td>
                              <td className="log-diff-new">{renderValue(newValue)}</td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : selectedActivity.details ? (
              <section className="activity-change-section">
                <div className="detail-section-heading">
                  <h3>Activity data</h3>
                  <span>Recorded values</span>
                </div>
                <div className="log-table-scroll">
                  <table className="log-table">
                    <thead>
                      <tr>
                        <th scope="col">Field</th>
                        <th scope="col">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(selectedActivity.details).map(
                        ([key, value]) => (
                          <tr key={key}>
                            <td>{key}</td>
                            <td>{renderValue(value)}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : (
              <p className="log-no-details">No additional activity data is available.</p>
            )}
          </section>
        )}
      </div>
    </main>
  );
}

export default Log;
